import type { ShareholderFormOutput } from '@/schemas/register.shareholder'
import type {
  AddCooperativeMembershipPartnerRequest,
  CooperativeMembershipDto,
  CooperativeMembershipWithoutPartnerDto,
  CreateShareholderRequest,
  CreateShareholderResponse,
  DeleteCooperativeMembershipParams,
  GetCooperativeMembershipParams,
  PartnerCostPreviewDto,
  PartnerDto,
  PartnerSettlementModel,
  PartnerSettlementPreviewDto,
  PatchCooperativeMembershipRequest,
  PostPartnerSettlementRequest,
  PostPartnerSettlementWithoutMembershipRequest,
  TenantId,
  UpdateForestOwnerGroupRequest,
} from '@/types/CooperativeMembership'
import type { ForestOwnerModel } from '@/types/ForestOwners/ForestOwner'
import { toNonPositiveNumber } from '@/utils/helper-functions'
import { getAnyPhoneNumberModel } from '@/utils/phone'
import { ApiClient, ApiError, apiGet, apiPatch, apiPost, apiPut } from './ApiClient'

export async function getCooperativeMembership(
  params: GetCooperativeMembershipParams,
): Promise<CooperativeMembershipDto> {
  const headers = params.tenantId ? { 'x-tenant-id': params.tenantId } : undefined

  return apiGet<CooperativeMembershipDto>(
    `/forestowners/${params.forestOwnerId}/cooperativeMembership`,
    headers ? { headers } : undefined,
  )
}

export async function getPartnerCostPreview(
  cooperativeMembershipId: string,
  partnerForestOwnerId: string,
): Promise<PartnerCostPreviewDto> {
  const searchParams = new URLSearchParams({ partnerForestOwnerId })

  return apiGet<PartnerCostPreviewDto>(
    `/cooperative-memberships/${encodeURIComponent(cooperativeMembershipId)}/partner/preview?${searchParams.toString()}`,
  )
}

/**
 * BE #4189 (`GET .../partner/settlement/preview`) has not shipped yet. Mocked via the
 * `partner-settlement-preview-default` MSW scenario (see docs/msw/partner-settlement-preview.md)
 * until the real endpoint exists. Response shape matches `PartnerSettlementPreviewModel` as
 * specified on that task — see `PartnerSettlementPreviewDto` in `types/CooperativeMembership.ts`.
 */
export async function getPartnerSettlementPreview(
  cooperativeMembershipId: string,
  partnerForestOwnerId: string,
  continuesAsMember = true,
): Promise<PartnerSettlementPreviewDto> {
  const searchParams = new URLSearchParams({
    partnerForestOwnerId,
    continuesAsMember: String(continuesAsMember),
  })

  return apiGet<PartnerSettlementPreviewDto>(
    `/cooperative-memberships/${encodeURIComponent(cooperativeMembershipId)}/partner/settlement/preview?${searchParams.toString()}`,
  )
}

export async function createPartnerSettlement(
  cooperativeMembershipId: string,
  request: PostPartnerSettlementRequest,
): Promise<PartnerSettlementModel | null> {
  const response = await ApiClient.post<PartnerSettlementModel | null>(
    `/cooperative-memberships/${encodeURIComponent(cooperativeMembershipId)}/partner/settlement`,
    request,
    { retries: 0 },
  )

  if (!response.ok || response.status !== 202) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data ?? null
}

export async function getPartnerSettlement(
  cooperativeMembershipId: string,
  settlementId: string,
): Promise<PartnerSettlementModel> {
  return apiGet<PartnerSettlementModel>(
    `/cooperative-memberships/${encodeURIComponent(cooperativeMembershipId)}/partner/settlement/${encodeURIComponent(settlementId)}`,
  )
}

export const PARTNER_REMOVE_SETTLEMENT_REASON = 'Partnership remove'
export const PARTNER_SETTLEMENT_POLL_MAX_ATTEMPTS = 10
export const PARTNER_SETTLEMENT_POLL_INTERVAL_MS = 1000

interface WaitForPartnerSettlementOptions {
  maxAttempts?: number
  intervalMs?: number
  signal?: AbortSignal
}

const abortableDelay = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason)
      return
    }
    const onAbort = () => {
      clearTimeout(timer)
      reject(signal?.reason)
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })

export async function waitForPartnerSettlementCompleted(
  cooperativeMembershipId: string,
  settlementId: string,
  {
    maxAttempts = PARTNER_SETTLEMENT_POLL_MAX_ATTEMPTS,
    intervalMs = PARTNER_SETTLEMENT_POLL_INTERVAL_MS,
    signal,
  }: WaitForPartnerSettlementOptions = {},
): Promise<PartnerSettlementModel> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    signal?.throwIfAborted()
    const settlement = await getPartnerSettlement(cooperativeMembershipId, settlementId)
    if (settlement.status === 'Completed') return settlement
    if (settlement.status === 'Failed') {
      throw new Error(settlement.error ?? 'Partner settlement failed')
    }
    if (attempt < maxAttempts - 1) {
      await abortableDelay(intervalMs, signal)
    }
  }
  throw new Error('Partner settlement did not complete in time')
}

export async function createPartnerSettlementWithoutMembership(
  cooperativeMembershipId: string,
  request: PostPartnerSettlementWithoutMembershipRequest,
): Promise<CooperativeMembershipWithoutPartnerDto> {
  const body: PostPartnerSettlementWithoutMembershipRequest = {
    partnerForestOwnerId: request.partnerForestOwnerId,
    continuesAsMember: false,
    reason: request.reason,
    ...(request.urlVerificationDocument !== undefined && {
      urlVerificationDocument: request.urlVerificationDocument,
    }),
  }
  const response = await ApiClient.post<CooperativeMembershipWithoutPartnerDto | null>(
    `/cooperative-memberships/${encodeURIComponent(cooperativeMembershipId)}/partner/settlement`,
    body,
    { retries: 0 },
  )

  if (!response.ok || response.status !== 200) {
    throw new ApiError(response.status, response.statusText, response.data)
  }
  if (response.data?.partner !== null) {
    throw new ApiError(response.status, 'Invalid partner settlement response', response.data)
  }

  return response.data
}

export async function getShareholder(shareholderId: string): Promise<CooperativeMembershipDto> {
  return apiGet<CooperativeMembershipDto>(`/cooperative-memberships/${shareholderId}`)
}

export async function createShareholder(
  formData: ShareholderFormOutput,
  forestOwnerData: ForestOwnerModel,
  tenantId: TenantId,
): Promise<CreateShareholderResponse> {
  const ownerType = formData.forestOwnerType
  const stepData =
    ownerType === 'organization'
      ? formData.step1Organization
      : ownerType === 'shared'
        ? formData.step1Sameie
        : formData.step1PrivatePerson

  const derivedSocialSecurityNumber = (() => {
    if (ownerType === 'private') {
      // Use the full 11-digit SSN directly
      const ssn = formData.step1PrivatePerson.ssn
      if (ssn && ssn.trim() !== '') {
        return ssn.replace(/[\s-]/g, '')
      }
    }

    return forestOwnerData.identificationNumber?.number ?? null
  })()

  const forestOwnerGroupNumberValue: string | null = (() => {
    const groupNumber =
      'forestOwnerGroupNumber' in stepData ? stepData.forestOwnerGroupNumber : undefined
    if (groupNumber && typeof groupNumber === 'string' && groupNumber.trim() !== '') {
      return groupNumber
    }
    return null
  })()

  const contactPerson: CreateShareholderRequest['contactPerson'] = {
    name: {
      firstName: forestOwnerData.name.firstName,
      lastName: forestOwnerData.name.lastName,
    },
    phoneNumber: getAnyPhoneNumberModel(forestOwnerData.contactPerson?.phoneNumber),
    email: forestOwnerData.email || '',
    socialSecurityNumber: derivedSocialSecurityNumber,
    birthDate: forestOwnerData.birthDate ?? undefined,
  }

  const requestBody: CreateShareholderRequest = {
    tenantId,
    forestOwner: {
      id: forestOwnerData.id,
      name: {
        firstName: forestOwnerData.name.firstName,
        lastName: forestOwnerData.name.lastName,
      },
      role: 'primary',
    },
    contactPerson,
    shareAccountNumber: stepData.accountNumber.replace(/\s/g, ''),
    capital: {
      // Backend requires shareCapital/extraCapital to be 0 or negative (a debt),
      // while the form displays these as positive amounts owed.
      shareCapital: toNonPositiveNumber(formData.step4?.shareCapital ?? 0),
      extraCapital: toNonPositiveNumber(formData.step4?.extraCapital ?? 0),
    },
    forestOwnerGroupNumber: forestOwnerGroupNumberValue,
  }

  try {
    return await apiPost<CreateShareholderResponse>('/cooperative-memberships', requestBody)
  } catch (error) {
    // Handle 409 Conflict - cooperative membership already exists
    if (error instanceof ApiError && error.status === 409) {
      const conflictData = error.data as { ResourceId?: string } | undefined
      if (conflictData?.ResourceId) {
        // Extract ID and perform PATCH to update existing membership
        const existingId = conflictData.ResourceId
        const existingMembership = await getShareholder(existingId)
        const response = await ApiClient.patch<CreateShareholderResponse>(
          `/cooperative-memberships/${existingId}`,
          requestBody,
          {
            headers: existingMembership.etag ? { 'If-Match': existingMembership.etag } : undefined,
          },
        )

        if (!response.ok) {
          throw new ApiError(response.status, response.statusText, response.data)
        }

        return response.data
      }
    }

    throw error
  }
}

export async function updateForestOwnerGroup(
  cooperativeMembershipId: string,
  forestOwnerGroupNumber: string,
  etag?: string | null,
): Promise<void> {
  const request: UpdateForestOwnerGroupRequest = { forestOwnerGroupNumber }
  await apiPut(
    `/cooperative-memberships/${encodeURIComponent(cooperativeMembershipId)}/forestOwnerGroup`,
    request,
    { etag },
  )
}

/**
 * Adds (or re-adds) the Partner-role forest owner on a cooperative membership
 * via the dedicated partner sub-resource. Returns `201 Created` even when the
 * same partner is posted again (no-op server-side).
 */
export async function addCooperativeMembershipPartner(
  cooperativeMembershipId: string,
  partnerForestOwnerId: string,
): Promise<PartnerDto> {
  const request: AddCooperativeMembershipPartnerRequest = { partnerForestOwnerId }

  return apiPost<PartnerDto>(
    `/cooperative-memberships/${encodeURIComponent(cooperativeMembershipId)}/partner`,
    request,
  )
}

/**
 * Sets or removes the Partner-role forest owner on a cooperative membership.
 * Pass `partnerId: null` to remove the current Partner (no-op if already absent).
 */
export async function updateCooperativeMembershipPartner(
  cooperativeMembershipId: string,
  partnerId: string | null,
  etag?: string | null,
): Promise<CooperativeMembershipDto> {
  const request: PatchCooperativeMembershipRequest = { partnerId }

  return apiPatch<CooperativeMembershipDto>(
    `/cooperative-memberships/${encodeURIComponent(cooperativeMembershipId)}`,
    request,
    { etag },
  )
}

export async function deleteCooperativeMembership({
  cooperativeMembershipId,
  tenantId,
  body,
  etag,
}: DeleteCooperativeMembershipParams): Promise<void> {
  const headers: Record<string, string> = {}

  if (typeof tenantId === 'string') {
    headers['x-tenant-id'] = tenantId
  }

  if (etag) {
    headers['If-Match'] = etag
  }

  const response = await ApiClient.delete(`/cooperative-memberships/${cooperativeMembershipId}`, {
    headers,
    body,
  })

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }
}
